/**
 * IndexedDB-кэш бинарных Fragments (.frag) по сигнатуре файла.
 *
 * Самая дорогая операция при открытии большой модели — это парсинг IFC и
 * конвертация в Fragments. `metaCache` кэширует только метаданные элементов,
 * но не геометрию, поэтому повторное открытие всё равно конвертирует заново.
 *
 * Здесь мы сохраняем уже сконвертированные .frag-байты (model.getBuffer()) и
 * при повторном открытии того же файла грузим их напрямую через
 * FragmentsManager, минуя web-ifc. Открытие становится мгновенным.
 *
 * Сигнатура совпадает с metaCache (`name::size::lastModified`); мост СОД
 * выставляет стабильный lastModified, поэтому кэш работает и для файлов из СОД.
 */

const DB_NAME = "ifc-viewer-frag-cache-v1";
const DB_VERSION = 1;
const STORE = "frag";
const PAYLOAD_VERSION = 1;
// Бюджет кэша: храним готовые модели, но не даём кэшу разрастаться без предела.
const MAX_TOTAL_BYTES = 600 * 1024 * 1024;

interface FragCacheEntry {
  signature: string;
  version: number;
  createdAt: number;
  byteLength: number;
  blob: Blob;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function isIndexedDbAvailable(): boolean {
  try {
    return typeof indexedDB !== "undefined" && indexedDB !== null;
  } catch (_) {
    return false;
  }
}

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) {
    return dbPromise;
  }
  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    if (!isIndexedDbAvailable()) {
      resolve(null);
      return;
    }
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (_) {
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "signature" });
        store.createIndex("createdAt", "createdAt", { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
  return dbPromise;
}

/** Возвращает сохранённые .frag-байты для файла или null. */
export async function loadFragFromCache(signature: string): Promise<ArrayBuffer | null> {
  const db = await openDb();
  if (!db) {
    return null;
  }
  const entry = await new Promise<FragCacheEntry | null>((resolve) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(STORE, "readonly");
    } catch (_) {
      resolve(null);
      return;
    }
    const req = tx.objectStore(STORE).get(signature);
    req.onsuccess = () => {
      const val = req.result as FragCacheEntry | undefined;
      if (!val || val.version !== PAYLOAD_VERSION || val.signature !== signature) {
        resolve(null);
        return;
      }
      resolve(val);
    };
    req.onerror = () => resolve(null);
  });
  if (!entry) {
    return null;
  }
  try {
    return await entry.blob.arrayBuffer();
  } catch (_) {
    return null;
  }
}

/** Сохраняет сконвертированные .frag-байты под сигнатурой файла. */
export async function saveFragToCache(signature: string, bytes: ArrayBuffer): Promise<void> {
  const db = await openDb();
  if (!db) {
    return;
  }
  const payload: FragCacheEntry = {
    signature,
    version: PAYLOAD_VERSION,
    createdAt: Date.now(),
    byteLength: bytes.byteLength,
    blob: new Blob([bytes]),
  };
  await new Promise<void>((resolve) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(STORE, "readwrite");
    } catch (_) {
      resolve();
      return;
    }
    const req = tx.objectStore(STORE).put(payload);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
  });
  void enforceCacheLimit(db);
}

/** Удаляет запись (например, если кэш оказался битым). */
export async function deleteFragFromCache(signature: string): Promise<void> {
  const db = await openDb();
  if (!db) {
    return;
  }
  await new Promise<void>((resolve) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(STORE, "readwrite");
    } catch (_) {
      resolve();
      return;
    }
    const req = tx.objectStore(STORE).delete(signature);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
  });
}

async function enforceCacheLimit(db: IDBDatabase): Promise<void> {
  try {
    const all = await new Promise<FragCacheEntry[]>((resolve) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve((req.result as FragCacheEntry[]) || []);
      req.onerror = () => resolve([]);
    });
    let total = all.reduce((sum, e) => sum + (e.byteLength || 0), 0);
    if (total <= MAX_TOTAL_BYTES) {
      return;
    }
    // Вытесняем самые старые записи, пока не уложимся в бюджет.
    all.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    const toDrop: string[] = [];
    for (const e of all) {
      if (total <= MAX_TOTAL_BYTES) {
        break;
      }
      toDrop.push(e.signature);
      total -= e.byteLength || 0;
    }
    if (toDrop.length === 0) {
      return;
    }
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      for (const sig of toDrop) {
        store.delete(sig);
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  } catch (_) {
    /* ignore */
  }
}
